/* AIMAK · arranque de la web.

   Flujo de entrada (igual que el prototipo, ahora contra la API):
     sin sesión            → login
     accesos vacíos        → pantalla de cero acceso (juego + solicitud a IT)
     primera vez hoy       → splash de bienvenida
     sin memoria           → onboarding con Makai
     lo demás              → shell con la vista de la ruta */
import "./tokens.css";
import "./fuentes.css";
import "./estilos.css";
import "./estilos-makai.css";
import "./estilos-finanzas.css";
import "./motion.css";
import { api, ApiError, type Job } from "./api";
import { estado, temaSiguiente } from "./estado";
import { MemoriaLocal, memoriaEsReal } from "./memoria";
import { ruta, ir, escuchar } from "./router";
import { esc, toast, modal, cerrarModal } from "./ui";
import { ponerIdioma } from "./i18n";
import type { Vista } from "./vistas/tipos";
import { shell, MODULO_DE, NOMBRE } from "./vistas/shell";
import { login } from "./vistas/login";
import { sinAcceso } from "./vistas/sinAcceso";
import { bienvenida } from "./vistas/bienvenida";
import { onboarding } from "./vistas/onboarding";
import { inicio } from "./vistas/inicio";
import { clientes, cliente } from "./vistas/clientes";
import { proyectos, proyecto } from "./vistas/proyectos";
import { jobs, job } from "./vistas/jobs";
import { ajustes } from "./vistas/ajustes";
import { auditoria } from "./vistas/auditoria";
import { memoria } from "./vistas/memoria";
import { bloqueado } from "./vistas/bloqueado";
import { nuevoProyecto } from "./vistas/nuevoProyecto";
import { calendario, workload } from "./vistas/calendario";
import { timesheet } from "./vistas/timesheet";
import { aprobaciones } from "./vistas/aprobaciones";
import { aiUsers } from "./vistas/aiusers";
import { ajustesExtra } from "./vistas/ajustesExtra";
import { matriz } from "./vistas/matriz";
import { calibracion } from "./vistas/calibracion";
import { montarFab } from "./fab";
import { makai } from "./vistas/makai";
import { conocimiento } from "./vistas/conocimiento";
import { finanzas, estimadoVista } from "./vistas/finanzas";
import { campana } from "./vistas/campana";
import { compliance } from "./vistas/compliance";
import { menciones } from "./vistas/mensajes";
import { chequeVista } from "./vistas/apar";
import { medios } from "./vistas/medios";
import { academy } from "./vistas/academy";
import { actividad } from "./vistas/actividad";
import { arrancarActividad, pararActividad } from "./actividad";
import { animar, transicion, montarOndas, temaColor } from "./motion";

let limpiezas: (() => void)[] = [];
let menuAbierto = false;
/* El cajón del móvil: abierto sobre el contenido. No persiste — se abre para
   ir a un sitio y se cierra al llegar. */
let cajon = false;
const ESTRECHO = "(max-width: 900px)";
const esEstrecho = () => window.matchMedia(ESTRECHO).matches;
let renderEnCurso = 0;
const alSalirDeVista = (fn: () => void) => { limpiezas.push(fn); };

const root = () => document.getElementById("app")!;

/* Mientras llega la primera vista: la marca se traza sola (motion.css). */
function pendiente(): Vista {
  return { full: '<div class="cargando"><div class="carga"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19V7l8 6 8-6v12"/></svg></div></div>' };
}

async function cargarSesion() {
  try { estado.me = await api.me(); } catch { estado.me = null; }
  if (estado.me && !estado.me.sinAcceso) {
    const m = await MemoriaLocal.cargar();
    estado.memoria = memoriaEsReal(m) ? m : null;
  } else estado.memoria = null;
}

async function entrar() {
  // Tras el login: recarga memoria y decide a dónde ir.
  await cargarSesion();
  const u = estado.usuario();
  if (u) void MemoriaLocal.evento("sesion", "Inició sesión");
  ir("#/inicio");
}

async function salir() {
  try { await api.logout(); } catch { /* la cookie ya no sirve */ }
  estado.me = null; estado.memoria = null; menuAbierto = false; cajon = false;
  pararActividad();
  ir("#/login");
}

async function vistaDeRuta(r: ReturnType<typeof ruta>): Promise<Vista> {
  const a = estado.accesos(); const mod = MODULO_DE[r.vista];
  // El monitor de SLA de jobs lo ven los directores con PM aunque no tengan Compliance.
  // Compliance: quien tiene el módulo gestiona; los demás ven sus propios incidentes y, con PM, el SLA de sus jobs.
  if (r.vista === "compliance") return compliance(r);
  if (mod && !(a.modulos as string[]).includes(mod)) return bloqueado(NOMBRE[r.vista] || r.vista);
  switch (r.vista) {
    case "inicio": return inicio();
    case "clientes": return r.a ? (r.b === "workload" ? workload(r.a, r.q) : cliente(r.a, r.b)) : clientes();
    case "proyectos": return r.a ? (r.a === "nuevo" ? nuevoProyecto() : proyecto(r.a, r.q)) : proyectos(r.q);
    case "menciones": return menciones();
    case "cheques": return r.a ? chequeVista(r.a) : finanzas(r);
    case "jobs": return r.a ? job(r.a, alSalirDeVista) : jobs();
    case "calendario": return calendario(r.q);
    case "timesheet": return timesheet(r.q);
    case "aprobaciones": return aprobaciones();
    case "ai-users": return aiUsers();
    case "ajustes": {
      // La calibración tiene pantalla propia: es una tabla ancha y una decisión
      // distinta de conceder accesos (F17).
      if (r.a === "calibracion") return calibracion();
      // La actividad de la casa (F24): la misma puerta por la que se conceden accesos.
      if (r.a === "actividad") return actividad(r);
      const v = await ajustes(render); const mx = await matriz(render); const x = await ajustesExtra();
      const d1 = v.despues, d2 = mx.despues, d3 = x.despues;
      return { ...v, html: (v.html || "") + (mx.html || "") + (x.html || ""), despues: (root) => { d1?.(root); d2?.(root); d3?.(root); } };
    }
    case "auditoria": return a.auditoria ? auditoria() : bloqueado("Auditoría");
    case "memoria": return memoria(render);
    case "makai": return makai(r, alSalirDeVista);
    case "conocimiento": return conocimiento(r);
    case "finanzas": return finanzas(r);
    case "estimados": return r.a ? estimadoVista(r.a) : finanzas(r);
    case "campanas": return r.a ? campana(r.a, r) : proyectos(r.q);
    case "medios": return medios(r);
    case "academy": return academy(r);
    case "compliance": return compliance(r);
    default: return inicio();
  }
}

async function render() {
  const id = ++renderEnCurso;
  limpiezas.forEach((f) => { try { f(); } catch { /* nada */ } }); limpiezas = [];
  cerrarModal();
  estado.aplicarTema();
  // La línea de progreso arriba de la hoja, mientras la vista pide sus datos.
  document.body.classList.add("cargando-vista");
  const r = ruta();
  const esAcademy = r.vista === "academy";
  const el = root();

  let v: Vista;
  try {
    if (!estado.me || r.vista === "login") {
      if (estado.me && r.vista === "login") { ir("#/inicio"); return; }
      v = login(() => void entrar());
    } else if (estado.me.sinAcceso && !esAcademy) {
      /* Con cero accesos la app manda a la pantalla del juego y la solicitud…
         menos si va al Academy: mientras IT decide, lo único que esta persona
         sí puede hacer es aprender cómo funciona la casa. Por lo mismo el
         Academy se salta el onboarding: aprender no espera a nada. */
      v = await sinAcceso(salir, () => void render(), alSalirDeVista);
    } else if (r.vista === "onboarding") {
      v = onboarding();
    } else if (!estado.pref.splashVisto[estado.me.usuario.id] || r.vista === "bienvenida") {
      v = await bienvenida();
    } else if (!estado.memoria && !esAcademy) {
      v = onboarding();
    } else {
      v = await vistaDeRuta(r);
    }
  } catch (e) {
    const err = e as ApiError;
    if (err.status === 401) { estado.me = null; ir("#/login"); return; }
    v = { titulo: "Algo salió mal", sub: "", html: '<div class="aviso crit"><b>' + esc(err.codigo || "error") + "</b> · " + esc(err.mensaje || err.message) + '</div><button class="btn" data-go="#/inicio" style="margin-top:12px">Volver a Inicio</button>' };
  }
  if (id !== renderEnCurso) return;   // llegó otra navegación mientras cargábamos

  let html: string;
  if (v.full) html = v.full;
  else {
    let badge = 0, badgeMenciones = 0;
    if ((estado.accesos().modulos as string[]).includes("pm")) {
      // Los favoritos viajan con los avisos: el panel lateral los pinta en el
      // mismo render, sin una vuelta más al servidor.
      try {
        const [js, mn, fav, enc] = await Promise.all([api.jobs(), api.menciones(true).catch(() => []), api.favoritos().catch(() => []), api.encargos().catch(() => [])]);
        // Una pregunta del equipo AI cuenta como una mención: es algo dirigido a mí que espera respuesta.
        badge = js.filter((j: Job) => j.estado === "sin_asignar").length; badgeMenciones = mn.length + enc.filter((e) => e.estado === "pregunta").length; estado.favoritos = fav;
      } catch { badge = 0; }
    }
    if (id !== renderEnCurso) return;
    html = shell(v, r, menuAbierto, badge, badgeMenciones, cajon);
  }
  /* Pintar es un solo gesto, dentro de una transición del documento (motion.ts):
     el shell se queda quieto, el contenido se desvanece y el elemento activo del
     menú se desliza a su sitio nuevo. Lo que sigue a la pintura —enlazar, el
     botón flotante, las cifras que cuentan— va dentro del mismo gesto. */
  const pintar = () => {
    estado.aplicarTema(); temaColor();
    document.body.classList.remove("cargando-vista");
    el.innerHTML = html; if (v.full) el.className = "";
    if (v.despues) v.despues(el);
    enlazar(el);
    if (!v.full && (estado.accesos().modulos as string[]).includes("timesheet")) alSalirDeVista(montarFab(el, r));
    animar(el);
  };
  await transicion(pintar);
  // El latido de presencia (F24): con sesión y dentro del shell, una vez por minuto.
  if (!v.full && estado.me && !estado.me.sinAcceso) arrancarActividad();
}

function enlazar(el: HTMLElement) {
  el.querySelectorAll<HTMLElement>("[data-go]").forEach((x) => { if (x.dataset.enlazado) return; x.dataset.enlazado = "1"; x.style.cursor = "pointer"; x.addEventListener("click", (e) => { e.stopPropagation(); menuAbierto = false; cajon = false; ir(x.dataset.go!); }); });
  const us = el.querySelector<HTMLElement>("#sh-usuario"); if (us) us.onclick = (e) => { if ((e.target as HTMLElement).closest(".menu-usuario")) return; menuAbierto = !menuAbierto; void render(); };
  const cerrarMenu = (e: MouseEvent) => { if (menuAbierto && !(e.target as HTMLElement).closest("#sh-usuario")) { menuAbierto = false; void render(); } };
  document.addEventListener("click", cerrarMenu); alSalirDeVista(() => document.removeEventListener("click", cerrarMenu));
  const sal = el.querySelector<HTMLElement>("#sh-salir"); if (sal) sal.onclick = () => void salir();
  // Los temas se rotan: papel → noche → Red Bull. El cambio entra con la
  // misma transición que una vista, así que los colores se funden, no saltan.
  const tema = el.querySelector<HTMLElement>("#sh-tema"); if (tema) tema.onclick = () => { estado.pref.tema = temaSiguiente(estado.pref.tema); estado.guardar(); menuAbierto = false; void render(); };
  // Un solo control para dos gestos: en pantalla estrecha abre y cierra el
  // cajón; en escritorio pliega el panel a iconos y lo recuerda.
  const panel = el.querySelector<HTMLElement>("#sh-panel");
  if (panel) panel.onclick = (e) => {
    e.stopPropagation();
    if (esEstrecho()) cajon = !cajon;
    else { estado.pref.panel = estado.pref.panel === "iconos" ? "completo" : "iconos"; estado.guardar(); }
    void render();
  };
  const velo = el.querySelector<HTMLElement>("#sh-velo");
  if (velo) velo.onclick = () => { cajon = false; void render(); };
  el.querySelectorAll<HTMLElement>("[data-idioma]").forEach((d) => d.onclick = () => { const i = d.dataset.idioma as "es" | "en"; estado.pref.idioma = i; estado.guardar(); ponerIdioma(i); toast(i === "en" ? "La interfaz del beta sigue en español: la traducción se hace de una sola pasada al final (ver docs/04-glosario)." : "Idioma: español."); void render(); });
}

// ------------------------------------------------------------ arranque
(async () => {
  root().innerHTML = pendiente().full!;
  estado.aplicarTema(); temaColor(); ponerIdioma(estado.pref.idioma);
  montarOndas();
  // Al ensanchar la ventana el cajón dejaría el panel abierto y el velo puesto
  // sobre un shell que ya no lo necesita.
  window.matchMedia(ESTRECHO).addEventListener("change", (e) => { if (!e.matches && cajon) { cajon = false; void render(); } });
  try { estado.tenant = await api.tenant(); }
  catch (e) {
    root().innerHTML = "";
    modal({ titulo: "No hay servidor", sub: "La web no pudo hablar con la API en /api. Levanta la API (puerto 3000) y recarga.", cuerpo: '<div class="aviso crit">' + esc((e as Error).message) + "</div>", botones: [{ t: "Reintentar", cls: "primario", fn: () => location.reload() }] });
    return;
  }
  document.title = estado.tenant.nombreApp + " · " + estado.tenant.nombre;
  await cargarSesion();
  if (!location.hash) location.hash = estado.me ? "#/inicio" : "#/login";
  escuchar(() => void render());
  await render();
})();
